'use client'

// This is a fork of the ChatMessageItem, ideally we should generic-fy ChatMessageItem to
// be generic in the future

import assistantMarkdown from '@/components/Chat/assistantMarkdown.module.css'
import AttachFileItem from '@/components/Chat/AttachFileItem'
import LoadingDots from '@/components/ui/loading-dots'
import { ChatItemV2 } from '@/lib/type'
import { sharedRehypeRewrite } from '@/lib/utils/markdownUtils'
import MarkdownPreview from '@uiw/react-markdown-preview'
import { AnimatePresence, motion } from 'framer-motion'
import Image from 'next/image'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { CopilotAgentContext, UserContext } from 'sprites-types/api/v2/copilot'

// Generic context display component
function ContextTags({
  context,
  initialWidth,
  contextRenderer,
}: {
  context: UserContext | CopilotAgentContext
  initialWidth?: number
  contextRenderer?: (context: UserContext | CopilotAgentContext, initialWidth?: number) => ReactNode
}) {
  if (contextRenderer) {
    return <div className="mt-2">{contextRenderer(context, initialWidth)}</div>
  }
  return null
}

const fadeAnimation = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.3 },
}

// Base message props
type BaseMessageProps = {
  chatMessage: ChatItemV2
  shouldAnimate?: boolean
  contextRenderer?: (context: UserContext | CopilotAgentContext, initialWidth?: number) => ReactNode
}

// User message component
function UserMessageWidget({
  chatMessage,
  shouldAnimate = true,
  contextRenderer,
}: BaseMessageProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [bubbleWidth, setBubbleWidth] = useState<number>()
  const bubbleRef = useRef<HTMLDivElement>(null)

  // Check if there's actually meaningful context to show
  const hasContextToShow =
    'context' in chatMessage &&
    chatMessage.context &&
    (() => {
      const context = chatMessage.context as UserContext
      const nodeIds = context.nodeIds || []
      return nodeIds.length > 0 || !!context.workflowRef
    })()

  // Measure bubble width when component mounts or window resizes
  useEffect(() => {
    const measureBubble = () => {
      if (bubbleRef.current) {
        const width = bubbleRef.current.getBoundingClientRect().width
        setBubbleWidth(width)
      }
    }

    measureBubble()
    window.addEventListener('resize', measureBubble)
    return () => window.removeEventListener('resize', measureBubble)
  }, [
    typeof chatMessage === 'object' &&
    'content' in chatMessage &&
    typeof chatMessage.content === 'string'
      ? chatMessage.content
      : '',
  ]) // Re-measure when message changes

  const renderMarkdownPreview = (content: string) => {
    // Preserve single newlines by converting them to markdown line breaks
    const processedContent = content.replace(/(?<!\n)\n(?!\n)/g, '  \n')

    return (
      <MarkdownPreview
        source={processedContent}
        style={{
          background: 'transparent',
          color: '#000',
        }}
        rehypeRewrite={sharedRehypeRewrite}
      />
    )
  }

  const bubbleStyle = {
    overflowWrap: 'break-word' as const,
    wordBreak: 'break-word' as const,
    fontFamily: 'Hanken Grotesk, sans-serif',
    width: 'fit-content',
    maxWidth: '100%',
  }

  // Only show UserMessage for user messages with content
  if (
    chatMessage.role !== 'user' ||
    typeof chatMessage.content !== 'string' ||
    !chatMessage.content
  ) {
    return null
  }

  const WrapperComponent = shouldAnimate ? motion.div : 'div'
  const animationProps = shouldAnimate ? fadeAnimation : {}

  return (
    <WrapperComponent {...animationProps}>
      <div className="flex w-full flex-row-reverse gap-x-1">
        <div className="flex grow flex-col gap-y-2 overflow-hidden">
          {chatMessage.context?.files && chatMessage.context.files.length > 0 && (
            <div className="flex flex-row-reverse flex-wrap gap-2">
              {chatMessage.context.files.map((file) => (
                <AttachFileItem
                  key={file.filename}
                  filename={file.filename}
                  isRemoveBtn={false}
                  isClickable={true}
                  onPreviewAction={() => {
                    window.open(file.url, '_blank')
                  }}
                />
              ))}
            </div>
          )}
          <div
            className="mb-5 max-w-[80%] self-end"
            onMouseEnter={() => {
              if (hasContextToShow) setIsHovered(true)
            }}
            onMouseLeave={() => {
              if (hasContextToShow) setIsHovered(false)
            }}
          >
            <motion.div
              ref={bubbleRef}
              className="font-hanken-grotesk relative rounded-3xl bg-[#ededed] px-4 py-3 text-black shadow-sm"
              style={bubbleStyle}
            >
              {renderMarkdownPreview(chatMessage.content || '')}
              <AnimatePresence>
                {isHovered &&
                  hasContextToShow &&
                  'context' in chatMessage &&
                  chatMessage.context && (
                    <div className="mt-2 max-w-full overflow-hidden">
                      <ContextTags
                        context={chatMessage.context}
                        initialWidth={bubbleWidth}
                        contextRenderer={contextRenderer}
                      />
                    </div>
                  )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </div>
    </WrapperComponent>
  )
}

// Assistant message component
function AssistantMessageWidget({ chatMessage, shouldAnimate = true }: BaseMessageProps) {
  let content: string
  if (chatMessage.role === 'system') {
    content =
      'message' in chatMessage && typeof chatMessage.message === 'string' ? chatMessage.message : ''
  } else if (chatMessage.role === 'assistant') {
    content = chatMessage.content || ''
  } else {
    content = ''
  }

  const renderMarkdownPreview = (content: string) => {
    // Preserve single newlines by converting them to markdown line breaks
    const processedContent = content.replace(/(?<!\n)\n(?!\n)/g, '  \n')

    return (
      <MarkdownPreview
        source={processedContent}
        className={assistantMarkdown['wm-markdown']}
        style={{
          background: 'transparent',
        }}
        rehypeRewrite={sharedRehypeRewrite}
      />
    )
  }

  const WrapperComponent = shouldAnimate ? motion.div : 'div'
  const animationProps = shouldAnimate ? fadeAnimation : {}

  return (
    <WrapperComponent {...animationProps}>
      <div className="flex w-full gap-x-1">
        <div className="flex grow flex-col gap-y-2 overflow-hidden">
          <div className="mb-5 max-w-[80%] self-start">
            <div
              className="font-hanken-grotesk relative rounded-3xl px-4 py-3 text-[#2D1A47]"
              style={{
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                fontFamily: 'Hanken Grotesk, sans-serif',
                width: 'fit-content',
                maxWidth: '100%',
              }}
            >
              {renderMarkdownPreview(content)}
            </div>
          </div>
        </div>
      </div>
    </WrapperComponent>
  )
}

// Loading message component
function LoadingMessageWidget({ shouldAnimate = true }: { shouldAnimate?: boolean }) {
  const WrapperComponent = shouldAnimate ? motion.div : 'div'
  const animationProps = shouldAnimate ? fadeAnimation : {}

  return (
    <AnimatePresence>
      <WrapperComponent {...animationProps}>
        <div className="flex items-center gap-x-2 self-start">
          <div className="h-8 w-8 shrink-0">
            <Image
              src="/sprites.svg"
              alt="Bot Avatar"
              width={32}
              height={32}
              className="h-full w-full scale-150"
            />
          </div>
          <div className="max-w-[85%] overflow-hidden rounded-xl rounded-tl-sm px-4 py-3 text-white">
            <div className="flex items-center">
              <LoadingDots size={8} color="#000000" />
            </div>
          </div>
        </div>
      </WrapperComponent>
    </AnimatePresence>
  )
}

// Custom content message component for special cases
function CustomContentMessageWidget({
  content,
  shouldAnimate = true,
}: {
  content: ReactNode
  shouldAnimate?: boolean
}) {
  const WrapperComponent = shouldAnimate ? motion.div : 'div'
  const animationProps = shouldAnimate ? fadeAnimation : {}

  return (
    <WrapperComponent {...animationProps}>
      <div className="flex w-full gap-x-1">
        <div className="flex grow flex-col gap-y-2 overflow-hidden">
          <div className="mb-5 max-w-[80%] self-start">
            <div
              className="font-hanken-grotesk relative rounded-3xl px-4 py-3 text-[#2D1A47]"
              style={{
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                fontFamily: 'Hanken Grotesk, sans-serif',
                width: 'fit-content',
                maxWidth: '100%',
              }}
            >
              {content}
            </div>
          </div>
        </div>
      </div>
    </WrapperComponent>
  )
}

// Main widget props
export type ChatMessageItemWidgetProps = {
  chatMessage: ChatItemV2
  showDividerAbove?: boolean
  shouldAnimate?: boolean

  // Rendering customization
  contextRenderer?: (context: UserContext | CopilotAgentContext, initialWidth?: number) => ReactNode
  customContentRenderer?: (chatMessage: ChatItemV2) => ReactNode | null
}

// Main generic chat message widget
export default function GraphicChatMessageItemWidget({
  chatMessage,
  showDividerAbove = false,
  shouldAnimate = true,
  contextRenderer,
  customContentRenderer,
}: ChatMessageItemWidgetProps) {
  // Check for custom content first
  const customContent = customContentRenderer?.(chatMessage)
  if (customContent) {
    return (
      <>
        {showDividerAbove && chatMessage.role === 'assistant' && (
          <div className="max-w-[80%]">
            <div className="my-2 border-t border-gray-600" />
          </div>
        )}
        <CustomContentMessageWidget content={customContent} shouldAnimate={shouldAnimate} />
      </>
    )
  }

  // Handle system loading messages
  if (
    chatMessage.role === 'system' &&
    !('message' in chatMessage && typeof chatMessage.message === 'string' && chatMessage.message)
  ) {
    return <LoadingMessageWidget shouldAnimate={shouldAnimate} />
  }

  // Show divider above assistant messages when requested
  const shouldShowDivider = chatMessage.role === 'assistant' && showDividerAbove

  // Determine which component to render based on role
  const isUserMessage = chatMessage.role === 'user'

  return (
    <>
      {shouldShowDivider && (
        <div className="max-w-[80%]">
          <div className="my-2 border-t border-gray-600" />
        </div>
      )}
      {isUserMessage ? (
        <UserMessageWidget
          chatMessage={chatMessage}
          shouldAnimate={shouldAnimate}
          contextRenderer={contextRenderer}
        />
      ) : (
        <AssistantMessageWidget chatMessage={chatMessage} shouldAnimate={shouldAnimate} />
      )}
    </>
  )
}

// Export individual components for more granular usage
export {
  AssistantMessageWidget,
  CustomContentMessageWidget,
  LoadingMessageWidget,
  UserMessageWidget,
}
